import React, { useEffect, useRef, useState } from "react";
import { X, Phone, ShieldCheck, RotateCw } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Reusable phone OTP modal.
 * Props:
 *  - open, onClose
 *  - phone: initial phone number
 *  - lang: "hi" | "en"
 *  - onVerified: (verification_token) => void
 */
export function OtpModal({ open, onClose, phone: initialPhone, lang = "hi", onVerified }) {
  const [phone, setPhone] = useState(initialPhone || "");
  const [step, setStep] = useState("request"); // request | verify
  const [otpId, setOtpId] = useState(null);
  const [code, setCode] = useState("");
  const [mockCode, setMockCode] = useState(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPhone(initialPhone || "");
      setStep("request");
      setCode("");
      setMockCode(null);
      setOtpId(null);
    }
  }, [open, initialPhone]);

  useEffect(() => {
    if (seconds > 0) {
      timerRef.current = setTimeout(() => setSeconds(seconds - 1), 1000);
      return () => clearTimeout(timerRef.current);
    }
  }, [seconds]);

  if (!open) return null;

  const request = async () => {
    if (!phone || phone.length < 10) { toast.error(lang==="hi"?"सही मोबाइल नंबर डालें":"Enter a valid phone"); return; }
    setSending(true);
    try {
      const r = await api.post("/otp/request", { phone });
      setOtpId(r.data.otp_id);
      setMockCode(r.data.mock_code || null);
      setStep("verify");
      setSeconds(60);
      toast.success(r.data.mock
        ? (lang==="hi" ? `OTP: ${r.data.mock_code} (डेमो)` : `OTP: ${r.data.mock_code} (demo)`)
        : (lang==="hi" ? "OTP भेज दिया गया" : "OTP sent"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    }
    setSending(false);
  };

  const verify = async () => {
    if (code.length < 4) { toast.error(lang==="hi"?"6-अंकों का OTP डालें":"Enter 6-digit OTP"); return; }
    setVerifying(true);
    try {
      const r = await api.post("/otp/verify", { otp_id: otpId, code });
      toast.success(lang==="hi"?"वेरिफ़ाई हो गया":"Verified");
      onVerified(r.data.verification_token, phone);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid OTP");
    }
    setVerifying(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-6 max-w-md w-full card-shadow" data-testid="otp-modal">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
              <ShieldCheck size={20}/>
            </div>
            <div>
              <h3 className="font-heading font-semibold">
                {lang==="hi"?"फ़ोन वेरिफ़िकेशन":"Phone Verification"}
              </h3>
              <p className="text-xs text-slate-500">
                {lang==="hi"?"बुकिंग सुरक्षित करने के लिए":"To secure your booking"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="otp-close">
            <X size={18}/>
          </button>
        </div>

        {step === "request" ? (
          <div className="mt-5 space-y-3">
            <label className="text-sm font-medium">{lang==="hi"?"मोबाइल नंबर":"Mobile number"}</label>
            <div className="flex items-center gap-2 rounded-xl bg-slate-50 border border-slate-200 px-3 py-3">
              <Phone size={18} className="text-slate-400"/>
              <span className="text-slate-500 text-sm">+91</span>
              <input value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g,""))}
                data-testid="otp-phone-input" maxLength={13}
                placeholder="98xxxxxxxx" className="flex-1 bg-transparent outline-none"/>
            </div>
            <button onClick={request} disabled={sending}
              data-testid="otp-send-btn"
              className="btn-primary w-full disabled:opacity-60">
              {sending ? "…" : (lang==="hi"?"OTP भेजें":"Send OTP")}
            </button>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            <p className="text-sm text-slate-600">
              {lang==="hi" ? `+91 ${phone} पर 6-अंकों का OTP भेजा गया।` : `6-digit OTP sent to +91 ${phone}.`}
            </p>
            {mockCode && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm">
                <span className="text-amber-700 font-medium">Demo OTP: </span>
                <span className="font-mono font-bold text-amber-900" data-testid="otp-mock-code">{mockCode}</span>
                <p className="text-[11px] text-amber-600 mt-1">
                  (SMS gateway not configured — code shown for testing)
                </p>
              </div>
            )}
            <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g,"").slice(0,6))}
              data-testid="otp-code-input" maxLength={6}
              placeholder="______"
              className="w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] outline-none focus:border-blue-500"/>

            <button onClick={verify} disabled={verifying}
              data-testid="otp-verify-btn"
              className="btn-primary w-full disabled:opacity-60">
              {verifying ? "…" : (lang==="hi"?"वेरिफ़ाई करें":"Verify")}
            </button>

            <button
              onClick={() => { if (seconds === 0) request(); }}
              disabled={seconds > 0}
              className="w-full text-sm text-slate-500 hover:text-blue-600 disabled:opacity-40 inline-flex items-center justify-center gap-1"
            >
              <RotateCw size={14}/>
              {seconds > 0
                ? (lang==="hi"?`फिर से भेजें (${seconds}s)`:`Resend in ${seconds}s`)
                : (lang==="hi"?"OTP फिर से भेजें":"Resend OTP")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Dynamically load Razorpay Checkout.js */
export function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}
